import JSZip from 'jszip';

const XML_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';

/** Retorna o conteúdo bruto da planilha (linhas e colunas) para visualização. */
export async function parsePlanilhaRaw(file: File): Promise<string[][]> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const rows =
    ext === 'csv'
      ? parseCsv(await file.text())
      : ext === 'xlsx' || ext === 'xls'
        ? await parseXlsx(file)
        : [];

  return trimEmptyTrailingRows(rows);
}

function trimEmptyTrailingRows(rows: string[][]): string[][] {
  let lastIndex = rows.length - 1;
  while (lastIndex >= 0 && rows[lastIndex].every((cell) => !String(cell ?? '').trim())) {
    lastIndex--;
  }
  return rows.slice(0, lastIndex + 1);
}

function parseCsv(text: string): string[][] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) return [];

  const delimiter = lines[0].includes(';') ? ';' : ',';
  return lines.map((line) => splitCsvLine(line, delimiter));
}

function splitCsvLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && char === delimiter) {
      result.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }

  result.push(current.trim());
  return result;
}

async function parseXlsx(file: File): Promise<string[][]> {
  const zip = await JSZip.loadAsync(file);
  const sharedStrings = await readSharedStrings(zip);
  const sheetPath =
    zip.file('xl/worksheets/sheet1.xml') ? 'xl/worksheets/sheet1.xml' : findFirstSheetPath(zip);
  if (!sheetPath) return [];

  const sheetXml = await zip.file(sheetPath)!.async('text');
  return parseSheetXml(sheetXml, sharedStrings);
}

function findFirstSheetPath(zip: JSZip): string | null {
  const paths = Object.keys(zip.files).filter((p) => /^xl\/worksheets\/sheet\d+\.xml$/.test(p));
  return paths.sort()[0] ?? null;
}

async function readSharedStrings(zip: JSZip): Promise<string[]> {
  const file = zip.file('xl/sharedStrings.xml');
  if (!file) return [];

  const xml = await file.async('text');
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const items = Array.from(doc.getElementsByTagNameNS(XML_NS, 'si'));

  return items.map((si) => {
    const direct = si.getElementsByTagNameNS(XML_NS, 't')[0]?.textContent;
    if (direct) return direct;

    const parts = Array.from(si.getElementsByTagNameNS(XML_NS, 't'));
    return parts.map((p) => p.textContent ?? '').join('');
  });
}

function parseSheetXml(xml: string, sharedStrings: string[]): string[][] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const cells = Array.from(doc.getElementsByTagNameNS(XML_NS, 'c'));
  const grid = new Map<number, Map<number, string>>();

  cells.forEach((cell) => {
    const ref = cell.getAttribute('r');
    if (!ref) return;

    const { row, col } = parseCellRef(ref);
    const value = readCellValue(cell, sharedStrings);
    if (!grid.has(row)) grid.set(row, new Map());
    grid.get(row)!.set(col, value);
  });

  const rowNumbers = Array.from(grid.keys()).sort((a, b) => a - b);
  if (rowNumbers.length === 0) return [];

  const maxCol = Math.max(
    ...rowNumbers.map((row) => Math.max(0, ...Array.from(grid.get(row)!.keys())))
  );

  return rowNumbers.map((row) => {
    const rowMap = grid.get(row)!;
    const values: string[] = [];
    for (let col = 0; col <= maxCol; col++) {
      values.push(rowMap.get(col) ?? '');
    }
    return values;
  });
}

function readCellValue(cell: Element, sharedStrings: string[]): string {
  const type = cell.getAttribute('t');
  const valueNode = cell.getElementsByTagNameNS(XML_NS, 'v')[0];
  const raw = valueNode?.textContent ?? '';

  if (type === 's') {
    const index = Number.parseInt(raw, 10);
    return Number.isNaN(index) ? '' : (sharedStrings[index] ?? '');
  }

  if (type === 'inlineStr') {
    return cell.getElementsByTagNameNS(XML_NS, 't')[0]?.textContent ?? '';
  }

  return raw;
}

function parseCellRef(ref: string): { row: number; col: number } {
  const match = ref.match(/^([A-Z]+)(\d+)$/i);
  if (!match) return { row: 0, col: 0 };

  const letters = match[1].toUpperCase();
  let col = 0;
  for (let i = 0; i < letters.length; i++) {
    col = col * 26 + (letters.charCodeAt(i) - 64);
  }

  return { row: Number.parseInt(match[2], 10) - 1, col: col - 1 };
}

export function colunaExcel(index: number): string {
  let n = index + 1;
  let label = '';
  while (n > 0) {
    const resto = (n - 1) % 26;
    label = String.fromCharCode(65 + resto) + label;
    n = Math.floor((n - 1) / 26);
  }
  return label;
}
